import { Stat, type Modifier, BASE_STATS } from '../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   La méta-progression (PLAN.md §6.9, amendé).

   ── POURQUOI TROIS MONNAIES ALORS QUE LE PLAN EN INTERDIT UNE TROISIÈME ──

   Le plan a raison sur le fond : la 3ᵉ monnaie est la faute n°1 des roguelites
   amateurs. Mais l'interdiction visait un symptôme précis — deux monnaies qui
   se gagnent PAREIL et se dépensent PAREIL, entre lesquelles le joueur ne sait
   pas laquelle il est en train de farmer.

   L'écart est accepté ici parce que les trois monnaies ont des PORTÉES
   disjointes, et qu'une portée se lit en une phrase :

     · CELLULES  — vivent le temps d'une run, se dépensent dans la boutique
                   de bord. Portée : la partie en cours.
     · CRÉDITS   — persistants, gagnés au SCORE, donc au VOLUME joué.
                   Portée : le déblocage de contenu (vaisseaux, armes).
     · POINTS DE COMPÉTENCE — persistants, gagnés à des JALONS DE MAÎTRISE.
                   Portée : l'arbre de statistiques.

   Test de lisibilité : « d'où vient cette monnaie ? » a une réponse d'un mot
   pour chacune (la run / le score / un exploit). Le jour où la réponse devient
   « ben, en jouant », la fusion des deux monnaies persistantes n'est plus une
   option, c'est une obligation.

   ── LE PIÈGE PRINCIPAL, ET LA PARADE STRUCTURELLE ──

   ⚠️ Un point de compétence doit récompenser la MAÎTRISE, jamais le TEMPS. Un
   jalon « détruire 10 000 ennemis au total » n'est pas un exploit : c'est un
   sablier. Il transforme les points en crédits déguisés, les deux monnaies
   persistantes redeviennent la même, et toute la justification ci-dessus
   s'effondre.

   La parade n'est pas une consigne dans un commentaire — les consignes ne
   survivent pas à six mois de contenu. Elle est dans le TYPE : `RunRecord` ne
   contient NI durée, NI compteur cumulé entre runs. Un jalon ne peut lire que
   ce qui s'est passé dans UNE run. Écrire un jalon chronométré demanderait
   d'ajouter un champ, c'est-à-dire de contourner sciemment la barrière — ce
   qu'une revue voit, contrairement à un oubli.

   Corollaire : le calcul des CRÉDITS, lui, reçoit la durée en argument séparé
   (l'anti-farm des runs de 20 secondes en a besoin). C'est volontairement
   asymétrique : la durée est légitime pour une monnaie de volume, illégitime
   pour une monnaie de maîtrise.

   ── LE PLAFOND ──

   ⚠️ La méta est une couche de RÉTENTION, jamais une condition de victoire. Un
   joueur excellent doit finir le jeu à méta zéro. D'où un plafond dur, vérifié
   en CI et affiché en permanence au joueur — la transparence est ce qui évite
   l'accusation de « mur de PV », et transforme le plafond en objectif de
   complétion plutôt qu'en frustration.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Les statistiques que la méta peut toucher ─────────────────────────────
   Volontairement TROIS, et pas les dix de `StatBlock`. Ouvrir la cadence ou le
   nombre de projectiles à la méta permettrait de construire hors-run un build
   que la run est censée faire mériter : la progression permanente écraserait
   la progression de partie, qui est le cœur du jeu. */
export const MetaStat = {
  Damage: 'damage',
  MaxHealth: 'maxHealth',
  MoveSpeed: 'moveSpeed',
} as const;

export type MetaStatId = (typeof MetaStat)[keyof typeof MetaStat];

/** Plafond par statistique (PLAN.md §6.9, valeurs reprises telles quelles). */
export const META_POWER_CAP: Readonly<Record<MetaStatId, number>> = {
  damage: 0.15,
  maxHealth: 0.2,
  moveSpeed: 0.08,
};

/** Plafond COMPOSITE affiché au joueur : « PUISSANCE PERMANENTE 22 % / 35 % ». */
export const META_COMPOSITE_CAP = 0.35;

/* Pondérations du composite.

   Une somme brute des trois bonus donnerait 0,43 et un produit 0,49 : ni l'un
   ni l'autre ne peut valoir 0,35, donc ni l'un ni l'autre n'est la grandeur que
   le plan appelle « composite ». Le composite est une mesure de PUISSANCE
   RESSENTIE, et les trois statistiques n'en donnent pas autant l'une que
   l'autre :

     · les dégâts raccourcissent chaque combat          → poids 1
     · la vitesse est la statistique d'esquive, celle
       qui décide si un rideau est franchissable        → poids 1
     · les PV n'accélèrent rien, ils achètent des
       secondes de survie sur les runs déjà perdues     → poids 0,6

   Le calibrage n'est pas arbitraire : ces poids-là sont choisis pour que
   l'arbre ENTIÈREMENT acheté vaille exactement 0,35, c'est-à-dire pour que le
   plafond soit atteint pile à la complétion. Une jauge qui s'arrêterait à 31 %
   laisserait croire à un contenu manquant ; une qui saturerait à 80 % de
   l'arbre rendrait les derniers nœuds inertes. */
export const COMPOSITE_WEIGHT: Readonly<Record<MetaStatId, number>> = {
  damage: 1,
  maxHealth: 0.6,
  moveSpeed: 1,
};

/* Tolérance de comparaison. 0.07 + 0.06 + 0.07 ne vaut pas 0.2 en IEEE-754, et
   une vérification stricte ferait échouer la CI sur un arbre pourtant exact.
   Le seuil est très en dessous du plus petit bonus réel (0,04), il ne peut donc
   pas masquer un vrai dépassement. */
const EPSILON = 1e-9;

/* ── Les bonus appliqués ──────────────────────────────────────────────────── */

/** Bonus permanents accumulés.
 *
 *  Les trois premiers champs sont les `MetaStat` qui pèsent sur la jauge de
 *  puissance (voir `compositePower`). Les deux derniers sont HORS jauge : ils
 *  améliorent les armes secondaires, un axe que `validateSecondaries` maintient
 *  sous la primaire, donc du confort tactique et non de la puissance au sens du
 *  plafond §6.9. */
export interface MetaBonus {
  damage: number;
  maxHealth: number;
  moveSpeed: number;
  /** Réduction de recharge des secondaires cumulée (0,15 = −15 %). */
  secondaryCooldown: number;
  /** Bonus de dégâts des secondaires cumulé (0,15 = +15 %). */
  secondaryDamage: number;
  /** Charges de bouclier PERMANENTES cumulées (nombre entier). HORS jauge, comme
   *  les secondaires : le bouclier est de la survie défensive, pas de la
   *  puissance sur l'axe équilibré — `compositePower` ne le somme pas. Sa borne
   *  dure vit dans `computeStats` (`STAT_CAPS.maxShield`), partagée avec la carte
   *  de run, si bien que méta + carte butent sur le même plafond. */
  shieldCharges: number;
  /** Régénération de bouclier permanente cumulée (charges/s). Même statut. */
  shieldRegen: number;
}

export function noBonus(): MetaBonus {
  return {
    damage: 0,
    maxHealth: 0,
    moveSpeed: 0,
    secondaryCooldown: 0,
    secondaryDamage: 0,
    shieldCharges: 0,
    shieldRegen: 0,
  };
}

/* ── Les nœuds de l'arbre ─────────────────────────────────────────────────── */

export const MetaCurrency = {
  /** Persistants, gagnés au score. Achètent du CONTENU. */
  Credits: 'credits',
  /** Persistants, gagnés aux jalons. Achètent de la PUISSANCE. */
  Skill: 'skill',
} as const;

export type MetaCurrencyId = (typeof MetaCurrency)[keyof typeof MetaCurrency];

/** Ce qu'un nœud « option » ouvre. Sert au tri visuel et au branchement :
 *  le registre de vaisseaux lit `ship`, le pool d'armes lit `weapon`. */
export const OptionKind = {
  Ship: 'ship',
  Weapon: 'weapon',
  Unlock: 'unlock',
} as const;

export type OptionKindId = (typeof OptionKind)[keyof typeof OptionKind];

interface NodeCommon {
  readonly id: string;
  readonly name: string;
  /** Texte joueur, à la première personne du jeu. */
  readonly text: string;
  /**
   * Nœud précédent, OBLIGATOIREMENT de la même branche.
   *
   * ⚠️ Le plan (§6.9) interdit de gater par statistique achetée — c'est
   * l'erreur documentée de Sky Force Reloaded. La contrainte est donc étroite :
   * un `after` ne sert qu'à ORDONNER les paliers d'une même branche (on achète
   * Dégâts II après Dégâts I, ce qui est de la lisibilité, pas du gating). Il
   * ne peut jamais désigner un nœud d'une autre branche ni un nœud option —
   * `verifyTree` le refuse.
   */
  readonly after: string | null;
  /** Jalons à accomplir. C'est le SEUL gate transversal autorisé : on ouvre par
   *  objectif accompli, jamais par achat. */
  readonly requires: readonly string[];
}

export interface StatNodeDef extends NodeCommon {
  readonly kind: 'stat';
  readonly currency: 'skill';
  readonly branch: MetaStatId;
  /** Fraction ajoutée à la branche. C'est cette valeur qui compte dans le
   *  plafond, indépendamment de la façon dont elle est ensuite appliquée. */
  readonly bonus: number;
  /** Coût en points de compétence. */
  readonly cost: number;
}

export interface OptionNodeDef extends NodeCommon {
  readonly kind: 'option';
  readonly currency: 'credits';
  readonly option: OptionKindId;
  /** Identifiant du contenu ouvert (vaisseau, arme, drapeau de déblocage). */
  readonly grants: string;
  /** Coût en crédits. */
  readonly cost: number;

  /* ── Amélioration PERMANENTE des armes secondaires ────────────────────────
     Deux champs facultatifs, et ils portent la DONNÉE d'une amélioration que
     `metaModifiers` traduit en modificateur (voir plus bas). La donnée vit dans
     le contenu (tree.def.ts), la mécanique dans le moteur — la même frontière
     que partout ailleurs.

     ⚠️ Ils ne pèsent PAS sur la jauge de +35 % : `compositePower` ne somme que
     les trois `MetaStat`. C'est volontaire et cohérent avec §6.9 — l'axe des
     secondaires est tenu sous la primaire par `validateSecondaries`, donc le
     buffer ne peut pas devenir une condition de victoire. Ce qu'on vend ici est
     l'affûtage d'un outil tactique, pas de la puissance sur l'axe équilibré. */

  /** Réduction permanente de la recharge des secondaires (0,15 = −15 %). */
  readonly secondaryCooldown?: number;
  /** Bonus permanent aux dégâts des secondaires (0,15 = +15 %). */
  readonly secondaryDamage?: number;

  /* ── Bouclier PERMANENT ────────────────────────────────────────────────────
     Même nature hors-jauge que les deux champs ci-dessus : le bouclier est de la
     survie défensive, pas de la puissance offensive. `metaBonuses` les additionne,
     `metaModifiers` les traduit en `maxShield`/`shieldRegen`, et `computeStats`
     leur applique la même borne dure (`STAT_CAPS.maxShield`) que la carte de run.
     Ils ne pèsent donc jamais sur le plafond §6.9. */

  /** Charges de bouclier permanentes accordées par le nœud (entier). */
  readonly shieldCharges?: number;
  /** Régénération de bouclier permanente accordée (charges/s). */
  readonly shieldRegen?: number;
}

export type MetaNodeDef = StatNodeDef | OptionNodeDef;

/* ── Les jalons de maîtrise ───────────────────────────────────────────────── */

/**
 * Ce qu'un jalon a le droit de lire.
 *
 * ⚠️ Aucun champ de DURÉE, aucun compteur CUMULÉ. Voir l'en-tête : c'est la
 * barrière de type qui empêche les points de compétence de dériver vers une
 * récompense de temps de jeu. Ajouter `durationSec` ou `totalKills` ici est le
 * geste exact qu'une revue doit refuser.
 */
export interface RunRecord {
  /** Mondes terminés DANS CETTE RUN. */
  readonly worldsCleared: number;
  /** La run est allée jusqu'au bout (victoire). */
  readonly cleared: boolean;
  readonly bossesKilled: number;
  /** Vies perdues. 0 = run propre. */
  readonly deaths: number;
  readonly bombsUsed: number;
  /** Coups encaissés, y compris ceux absorbés sans perdre de vie. */
  readonly hitsTaken: number;
  readonly kills: number;
  readonly graze: number;
  /** 0 = Recrue, 1 = Normal, 2 = Vétéran. */
  readonly difficulty: number;
  /** Cellules dépensées à la boutique de bord. */
  readonly cellsSpent: number;
}

/**
 * Condition d'un jalon, sous forme déclarative.
 *
 * Un prédicat `(run) => boolean` aurait été plus souple — trop : il aurait
 * permis d'écrire n'importe quoi, y compris de refermer par la fenêtre la
 * porte de la durée fermée par `RunRecord`. Une condition déclarative se lit,
 * se sérialise, et se vérifie en CI.
 *
 * Tous les champs renseignés doivent être satisfaits (ET logique). Un champ
 * absent ne contraint rien.
 */
export interface MilestoneCond {
  readonly minWorldsCleared?: number;
  readonly cleared?: boolean;
  readonly minBossesKilled?: number;
  readonly maxDeaths?: number;
  readonly maxBombsUsed?: number;
  readonly maxHitsTaken?: number;
  readonly minKills?: number;
  readonly minGraze?: number;
  readonly minDifficulty?: number;
  readonly maxCellsSpent?: number;
}

export interface MilestoneDef {
  readonly id: string;
  readonly name: string;
  readonly text: string;
  /** Points de compétence versés. UNE seule fois, à vie — un jalon répétable
   *  serait un revenu, donc du temps déguisé en maîtrise. */
  readonly points: number;
  readonly cond: MilestoneCond;
}

/* ── L'état persistant, tel que la méta le voit ──────────────────────────── */

export interface MetaState {
  readonly credits: number;
  /** Jalons déjà décrochés (identifiants). */
  readonly milestones: readonly string[];
  /** Nœuds achetés (identifiants). */
  readonly nodes: readonly string[];
}

export function emptyMetaState(): MetaState {
  return { credits: 0, milestones: [], nodes: [] };
}

/* ── Évaluation des jalons ────────────────────────────────────────────────── */

function satisfies(cond: MilestoneCond, run: RunRecord): boolean {
  if (cond.minWorldsCleared !== undefined && run.worldsCleared < cond.minWorldsCleared) return false;
  if (cond.cleared !== undefined && run.cleared !== cond.cleared) return false;
  if (cond.minBossesKilled !== undefined && run.bossesKilled < cond.minBossesKilled) return false;
  if (cond.maxDeaths !== undefined && run.deaths > cond.maxDeaths) return false;
  if (cond.maxBombsUsed !== undefined && run.bombsUsed > cond.maxBombsUsed) return false;
  if (cond.maxHitsTaken !== undefined && run.hitsTaken > cond.maxHitsTaken) return false;
  if (cond.minKills !== undefined && run.kills < cond.minKills) return false;
  if (cond.minGraze !== undefined && run.graze < cond.minGraze) return false;
  if (cond.minDifficulty !== undefined && run.difficulty < cond.minDifficulty) return false;
  if (cond.maxCellsSpent !== undefined && run.cellsSpent > cond.maxCellsSpent) return false;
  return true;
}

/**
 * Jalons NOUVELLEMENT décrochés par cette run.
 *
 * Renvoie la différence, pas l'ensemble : c'est ce dont l'écran de fin a besoin
 * pour n'afficher que ce qui vient d'arriver. Recalculer l'ensemble complet et
 * laisser l'appelant faire la soustraction inviterait à oublier le filtrage —
 * et à re-verser les points à chaque run, ce qui est exactement le revenu
 * automatique qu'on cherche à interdire.
 */
export function newlyEarned(
  defs: readonly MilestoneDef[],
  run: RunRecord,
  already: ReadonlySet<string>,
): MilestoneDef[] {
  const out: MilestoneDef[] = [];
  for (const def of defs) {
    if (already.has(def.id)) continue;
    if (satisfies(def.cond, run)) out.push(def);
  }
  return out;
}

/** Total de points gagnés à vie. */
export function skillPointsEarned(
  defs: readonly MilestoneDef[],
  earned: ReadonlySet<string>,
): number {
  let total = 0;
  for (const def of defs) if (earned.has(def.id)) total += def.points;
  return total;
}

/** Points immobilisés dans les nœuds déjà achetés. */
export function skillPointsSpent(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
): number {
  let total = 0;
  for (const n of nodes) if (n.kind === 'stat' && bought.has(n.id)) total += n.cost;
  return total;
}

export function skillPointsAvailable(
  milestones: readonly MilestoneDef[],
  nodes: readonly MetaNodeDef[],
  state: MetaState,
): number {
  const earned = new Set(state.milestones);
  const bought = new Set(state.nodes);
  return skillPointsEarned(milestones, earned) - skillPointsSpent(nodes, bought);
}

/* ── Composition des bonus ───────────────────────────────────────────────── */

/**
 * Somme des bonus des nœuds achetés, PUIS écrêtage par statistique.
 *
 * L'écrêtage est ici et pas seulement en CI : la vérification statique protège
 * l'arbre livré, elle ne protège pas une sauvegarde importée à la main ni un
 * arbre modifié par un futur contenu additionnel. Le plafond doit tenir même
 * quand la donnée ment.
 */
export function metaBonuses(nodes: readonly MetaNodeDef[], bought: ReadonlySet<string>): MetaBonus {
  const out = noBonus();
  for (const n of nodes) {
    if (!bought.has(n.id)) continue;
    if (n.kind === 'stat') {
      out[n.branch] = out[n.branch] + n.bonus;
    } else {
      // Nœuds OPTION portant une amélioration permanente de secondaire. Ils
      // s'additionnent entre eux ; leur borne DURE n'est pas ici mais dans
      // `computeStats` (plafond des dégâts, plancher de la recharge), pour que
      // le même garde-fou tienne quel que soit le nombre de sources.
      if (n.secondaryCooldown) out.secondaryCooldown = out.secondaryCooldown + n.secondaryCooldown;
      if (n.secondaryDamage) out.secondaryDamage = out.secondaryDamage + n.secondaryDamage;
      if (n.shieldCharges) out.shieldCharges = out.shieldCharges + n.shieldCharges;
      if (n.shieldRegen) out.shieldRegen = out.shieldRegen + n.shieldRegen;
    }
  }
  // Écrêtage des seules branches de PUISSANCE (les trois `MetaStat`). Les bonus
  // de secondaire n'ont pas de plafond de branche : ils ne comptent pas dans la
  // jauge de +35 %, et leur borne vit dans `computeStats`.
  for (const k of Object.keys(META_POWER_CAP) as MetaStatId[]) {
    const cap = META_POWER_CAP[k];
    if (out[k] > cap) out[k] = cap;
    if (out[k] < 0) out[k] = 0;
  }
  return out;
}

/** La grandeur affichée dans la jauge. Voir `COMPOSITE_WEIGHT` pour le pourquoi
 *  de la pondération. */
export function compositePower(b: MetaBonus): number {
  let sum = 0;
  // ⚠️ On itère les CLÉS PONDÉRÉES, pas celles de `b` : depuis que `MetaBonus`
  // porte aussi des bonus de secondaire (hors jauge), parcourir `b` multiplierait
  // par un `COMPOSITE_WEIGHT` absent (NaN) et ferait mentir la jauge. Seules les
  // trois `MetaStat` pèsent sur la puissance affichée.
  for (const k of Object.keys(COMPOSITE_WEIGHT) as MetaStatId[]) sum += b[k] * COMPOSITE_WEIGHT[k];
  return sum;
}

export interface PowerGauge {
  /** Composite courant, en fraction (0,22 = 22 %). */
  readonly value: number;
  readonly cap: number;
  /** Remplissage de la barre, dans [0,1]. */
  readonly ratio: number;
}

/** Ce que le HUD et l'écran de méta affichent EN PERMANENCE. Le plan en fait
 *  une décision de transparence : un joueur qui sait qu'il ne peut pas acheter
 *  la victoire n'accuse pas le jeu d'être un mur de PV. */
export function powerGauge(b: MetaBonus): PowerGauge {
  const value = compositePower(b);
  const ratio = value <= 0 ? 0 : value >= META_COMPOSITE_CAP ? 1 : value / META_COMPOSITE_CAP;
  return { value, cap: META_COMPOSITE_CAP, ratio };
}

/**
 * Vies offertes par la branche PV.
 *
 * ⚠️ Le piège du jeu à vies entières. Les PV du plan sont ici des VIES, et une
 * vie ne se coupe pas en morceaux : +20 % sur une base de 3 donne 3,6.
 *
 *   · arrondir vers le bas donne toujours 3 → la branche entière ne verse
 *     jamais rien, six points de compétence achètent le néant, et le plan
 *     interdit formellement qu'un achat soit un piège mort (§6.7) ;
 *   · arrondir au plus proche donne 4 → la branche complète vaut réellement
 *     +33 % de tentatives, au-delà de l'intention des +20 %.
 *
 * On tranche pour l'arrondi au plus proche, en connaissance de cause : mieux
 * vaut un léger dépassement sur une statistique qui n'accélère aucun combat
 * qu'une branche inerte. C'est d'ailleurs pourquoi les PV pèsent 0,6 dans le
 * composite — le sur-versement y est déjà partiellement compensé.
 *
 * Conséquence assumée et à dire au joueur dans le texte du nœud : la branche
 * paie D'UN COUP, à son dernier palier. Le texte des paliers 1 et 2 doit donc
 * annoncer la progression, pas un gain immédiat.
 */
export function metaLivesBonus(b: MetaBonus, baseLives: number = BASE_STATS.maxLives): number {
  return Math.round(baseLives * b.maxHealth);
}

/**
 * Traduction des bonus méta en modificateurs pour la simulation.
 *
 * On passe par `Modifier` plutôt que d'écrire dans `StatBlock` pour que la méta
 * emprunte exactement le même chemin que les cartes ramassées en run : additifs
 * d'abord, multiplicatifs ensuite, plafonds durs à la fin. Un chemin séparé
 * finirait par diverger — et un écart d'ordre d'application rendrait deux
 * builds identiques inégaux, ce qui est indéfendable dans un jeu à score.
 *
 * Les vies passent en ADDITIF ENTIER, jamais en multiplicatif : un multiplicatif
 * produirait 3,6 vies, et `computeStats` n'arrondit que `bulletCount`.
 */
export function metaModifiers(b: MetaBonus, baseLives: number = BASE_STATS.maxLives): Modifier[] {
  const out: Modifier[] = [];
  if (b.damage > 0) out.push({ stat: Stat.Damage, op: 'mul', value: b.damage });
  if (b.moveSpeed > 0) out.push({ stat: Stat.MoveSpeed, op: 'mul', value: b.moveSpeed });
  const lives = metaLivesBonus(b, baseLives);
  if (lives > 0) out.push({ stat: Stat.MaxLives, op: 'add', value: lives });
  /* Améliorations permanentes de secondaire, achetées au hangar. La recharge
     est un multiplicateur où PLUS BAS = MIEUX : une réduction de 0,15 devient un
     `mul` NÉGATIF (`1 × 0,85`). Absentes d'une run sans achat — donc `baseMods`
     reste vide, ce que la jonction méta vérifie. */
  if (b.secondaryCooldown > 0) {
    out.push({ stat: Stat.SecondaryCooldownMul, op: 'mul', value: -b.secondaryCooldown });
  }
  if (b.secondaryDamage > 0) {
    out.push({ stat: Stat.SecondaryDamageMul, op: 'mul', value: b.secondaryDamage });
  }
  /* Bouclier permanent, en ADDITIF comme la carte de run : `maxShield` part de
     zéro, un `mul` sur zéro ne débloquerait jamais rien. Le plafond dur
     (`STAT_CAPS.maxShield`) est appliqué par `computeStats`, donc méta + carte
     ne peuvent pas dépasser la barrière commune. */
  if (b.shieldCharges > 0) {
    out.push({ stat: Stat.MaxShield, op: 'add', value: b.shieldCharges });
  }
  if (b.shieldRegen > 0) {
    out.push({ stat: Stat.ShieldRegen, op: 'add', value: b.shieldRegen });
  }
  return out;
}

/* ── Achat ────────────────────────────────────────────────────────────────── */

export interface PurchaseVerdict {
  readonly ok: boolean;
  /** Motif de refus, prêt à afficher. Vide si `ok`. */
  readonly reason: string;
}

const OK: PurchaseVerdict = { ok: true, reason: '' };

/**
 * Le nœud peut-il être acheté maintenant ?
 *
 * Une seule fonction pour l'UI (griser le bouton) et pour la transaction
 * (refuser). Deux implémentations divergeraient tôt ou tard, et la divergence
 * se paierait soit en nœud achetable mais grisé, soit — bien pire — en nœud
 * grisé mais achetable par une UI un peu trop rapide.
 */
export function canPurchase(
  node: MetaNodeDef,
  state: MetaState,
  skillAvailable: number,
): PurchaseVerdict {
  if (state.nodes.includes(node.id)) return { ok: false, reason: 'Déjà acquis.' };

  const earned = new Set(state.milestones);
  for (const req of node.requires) {
    if (!earned.has(req)) return { ok: false, reason: 'Jalon de maîtrise non accompli.' };
  }
  if (node.after !== null && !state.nodes.includes(node.after)) {
    return { ok: false, reason: 'Palier précédent non acquis.' };
  }
  if (node.kind === 'stat') {
    if (skillAvailable < node.cost) {
      return { ok: false, reason: 'Points de compétence insuffisants.' };
    }
  } else if (state.credits < node.cost) {
    return { ok: false, reason: 'Crédits insuffisants.' };
  }
  return OK;
}

/** Applique l'achat. Renvoie un état NEUF — la sauvegarde est traitée comme
 *  immuable pour que `SaveStore.save(patch)` reste une simple fusion. */
export function purchase(node: MetaNodeDef, state: MetaState): MetaState {
  return {
    credits: node.kind === 'option' ? state.credits - node.cost : state.credits,
    milestones: state.milestones,
    nodes: [...state.nodes, node.id],
  };
}

/**
 * Rend tous les nœuds STAT et rembourse les points.
 *
 * Gratuit et illimité, comme le plan l'exige. Un respec payant transforme
 * l'expérimentation en risque financier : le joueur cesse d'essayer, et un
 * arbre qu'on n'explore pas n'a aucune raison d'exister. Les nœuds OPTION ne
 * sont pas rendus — ce sont des déblocages de contenu, pas des choix.
 */
export function respec(nodes: readonly MetaNodeDef[], state: MetaState): MetaState {
  const stat = new Set<string>();
  for (const n of nodes) if (n.kind === 'stat') stat.add(n.id);
  return {
    credits: state.credits,
    milestones: state.milestones,
    nodes: state.nodes.filter((id) => !stat.has(id)),
  };
}

/* ── Crédits ──────────────────────────────────────────────────────────────── */

/** Multiplicateurs de difficulté (PLAN.md §6.9). */
export const DIFFICULTY_MUL: readonly number[] = [0.7, 1.0, 1.45];

/**
 * Plafond DUR du multiplicateur de crédits d'une run (PLAN.md §6.9, repris par
 * le texte du nœud « Double contrainte » : « Gains cumulés, plafond ×3,0 »).
 *
 * ⚠️ Il porte sur le TOTAL — difficulté × contraintes — et pas seulement sur la
 * part des contraintes. La lecture inverse était tentante et elle rendait le
 * plafond DÉCORATIF : la somme des quatre bonus vaut 1 + 0,30 + 0,35 + 0,45 +
 * 0,80 = 2,90, et le cumul est de toute façon limité à deux contraintes, donc
 * la part « contraintes » ne peut jamais atteindre 3,0 toute seule. Un plafond
 * qui ne mord jamais n'est pas un plafond, c'est un commentaire.
 *
 * Sur le total, il mord exactement là où il doit : Vétéran + Coque de verre +
 * Marée noire donne 1,45 × 2,25 = 3,2625, ramené à 3,0. Le meilleur farm
 * possible rapporte donc trois fois une run de référence, jamais davantage.
 */
export const CREDIT_MUL_CAP = 3.0;

/** Contraintes portables sur une run, selon que « Double contrainte » est
 *  acheté ou non. Le nœud vend le droit d'en cumuler DEUX — pas quatre. */
export const MAX_CONSTRAINTS = 1;
export const MAX_CONSTRAINTS_WITH_CUMUL = 2;

export function maxConstraints(cumulUnlocked: boolean): number {
  return cumulUnlocked ? MAX_CONSTRAINTS_WITH_CUMUL : MAX_CONSTRAINTS;
}

/**
 * Multiplicateur de crédits d'une run.
 *
 * ── LA RÈGLE DE COMPOSITION, ET POURQUOI CELLE-LÀ ──
 *
 * Entre CONTRAINTES : ADDITIF. « Gains cumulés » se lit ainsi, et surtout
 * l'additif est le seul mode où le nombre affiché sur le nœud reste vrai après
 * l'achat du suivant. En multiplicatif, prendre Marée noire (+45 %) après Coque
 * de verre (+80 %) rapporterait 1,45 × 1,80 = 2,61 au lieu de 2,25 : la seconde
 * contrainte vaudrait +81 points de pourcentage alors que son texte en promet
 * 45, et le joueur ne pourrait pas vérifier le compte de tête. Le cumul serait
 * en outre super-linéaire — donc récompenserait l'empilement plus que la somme
 * de ses parties, ce qui est précisément l'amplificateur de farm que le plafond
 * existe pour empêcher.
 *
 * Avec la DIFFICULTÉ : MULTIPLICATIF. Les deux axes sont orthogonaux — un
 * palier dit à quel point les ennemis sont durs, une contrainte ce dont le
 * joueur se prive. L'additif ici produirait une inversion franche : Recrue +
 * Marée noire vaudrait 0,70 + 0,45 = 1,15 (+64 % de revenu) contre 1,45 + 0,45
 * = 1,90 pour le Vétéran (+31 %). La contrainte serait donc deux fois plus
 * rentable au palier le plus facile, et le farm optimal consisterait à jouer en
 * Recrue. En multiplicatif, elle vaut exactement +45 % à tous les paliers et le
 * Vétéran reste strictement le mieux payé — ce que le plan exige.
 *
 * Puis ÉCRÊTAGE à `CREDIT_MUL_CAP`.
 *
 * ⚠️ L'écrêtage est ici et pas seulement dans l'interface, pour la même raison
 * que `metaBonuses` écrête ses branches : la vérification en amont protège le
 * jeu livré, pas une sauvegarde bricolée ni un écran de briefing qui laisserait
 * passer trois contraintes après un futur remaniement. Le plafond doit tenir
 * même quand la donnée ment.
 */
export function creditMultiplier(difficulty: number, constraintBonuses: readonly number[]): number {
  const diff = DIFFICULTY_MUL[difficulty] ?? 1;
  let sum = 0;
  // Les bonus négatifs sont ignorés plutôt que soustraits : une « contrainte »
  // qui réduirait le gain n'a aucun sens et serait le signe d'une donnée
  // corrompue, pas d'une intention de conception.
  for (const b of constraintBonuses) if (b > 0) sum += b;
  const mul = diff * (1 + sum);
  return mul > CREDIT_MUL_CAP ? CREDIT_MUL_CAP : mul;
}

/** En dessous, la run ne rapporte RIEN. Sans ce seuil, mourir volontairement en
 *  vingt secondes devient la façon la plus rentable de gagner des crédits —
 *  bug économique classique qui vide le jeu de son contenu. */
export const MIN_RUN_SECONDS = 90;

/**
 * Crédits gagnés par une run.
 *
 * ⚠️ `durationSec` est un argument SÉPARÉ, volontairement absent de
 * `RunRecord`. Les crédits sont une monnaie de volume : le temps y a sa place,
 * et l'anti-farm en a besoin. Les jalons sont une monnaie de maîtrise : le
 * temps n'y a aucune place. Fusionner les deux signatures ferait entrer la
 * durée dans le champ de vision des jalons, et c'est précisément par là que
 * l'économie se dérègle.
 *
 * ⚠️ `constraintBonuses` reste lui aussi un argument SÉPARÉ, et absent de
 * `RunRecord`. Même barrière de type que la durée, dans l'autre sens : un jalon
 * ne doit pas pouvoir exiger une contrainte volontaire, sinon la maîtrise
 * s'achète à la boutique — 850 crédits pour Coque de verre, et le jalon « finir
 * sans mourir » devient une formalité qu'on a payée.
 */
export function creditsForRun(
  score: number,
  run: RunRecord,
  durationSec: number,
  constraintBonuses: readonly number[] = [],
): number {
  if (durationSec < MIN_RUN_SECONDS) return 0;
  const mul = creditMultiplier(run.difficulty, constraintBonuses);
  const raw = Math.floor(score / 2000) + 120 * run.worldsCleared + 200 * run.bossesKilled;
  return Math.floor(raw * mul);
}

/* ── Vérifications ────────────────────────────────────────────────────────── */

/** Somme brute d'une branche, SANS écrêtage — c'est la valeur qu'on veut
 *  vérifier en CI. Passer par `metaBonuses` masquerait le dépassement,
 *  puisqu'elle écrête justement. */
export function branchTotal(nodes: readonly MetaNodeDef[], branch: MetaStatId): number {
  let sum = 0;
  for (const n of nodes) if (n.kind === 'stat' && n.branch === branch) sum += n.bonus;
  return sum;
}

export function totalSkillCost(nodes: readonly MetaNodeDef[]): number {
  let sum = 0;
  for (const n of nodes) if (n.kind === 'stat') sum += n.cost;
  return sum;
}

export function totalCreditCost(nodes: readonly MetaNodeDef[]): number {
  let sum = 0;
  for (const n of nodes) if (n.kind === 'option') sum += n.cost;
  return sum;
}

/**
 * Contrôle intégral de l'arbre et des jalons. Renvoie la liste des problèmes.
 *
 * Destinée à la CI et à un appel au chargement : le plan exige une assertion
 * automatique que « la somme des nœuds achetables ne peut pas dépasser le
 * plafond ». Sans elle, un futur nœud à +5 % de dégâts ajouté de bonne foi
 * casse silencieusement la promesse faite au joueur sur l'écran de méta — et
 * personne ne s'en aperçoit avant qu'un joueur ne calcule.
 */
export function verifyTree(
  nodes: readonly MetaNodeDef[],
  milestones: readonly MilestoneDef[],
): string[] {
  const problems: string[] = [];

  const byId = new Map<string, MetaNodeDef>();
  for (const n of nodes) {
    if (byId.has(n.id)) problems.push(`nœud dupliqué : ${n.id}`);
    byId.set(n.id, n);
    if (n.cost <= 0) problems.push(`${n.id} : coût doit être > 0`);
    if (n.kind === 'stat' && n.bonus <= 0) problems.push(`${n.id} : bonus doit être > 0`);
  }

  const milestoneIds = new Set<string>();
  for (const m of milestones) {
    if (milestoneIds.has(m.id)) problems.push(`jalon dupliqué : ${m.id}`);
    milestoneIds.add(m.id);
    if (m.points <= 0) problems.push(`jalon ${m.id} : points doit être > 0`);
    if (Object.keys(m.cond).length === 0) {
      // Une condition vide serait satisfaite par n'importe quelle run, y
      // compris un abandon immédiat : des points gratuits à chaque partie.
      problems.push(`jalon ${m.id} : condition vide, décroché par toute run`);
    }
  }

  for (const n of nodes) {
    for (const req of n.requires) {
      if (!milestoneIds.has(req)) problems.push(`${n.id} : jalon inconnu « ${req} »`);
    }
    if (n.after === null) continue;
    const prev = byId.get(n.after);
    if (!prev) {
      problems.push(`${n.id} : prédécesseur inconnu « ${n.after} »`);
    } else if (n.kind !== 'stat' || prev.kind !== 'stat' || prev.branch !== n.branch) {
      // Le gating par achat est l'erreur documentée de Sky Force Reloaded : on
      // n'ouvre jamais du contenu en échange d'une statistique payée.
      problems.push(`${n.id} : « after » doit rester dans la même branche STAT`);
    }
  }

  for (const k of Object.keys(META_POWER_CAP) as MetaStatId[]) {
    const total = branchTotal(nodes, k);
    if (total > META_POWER_CAP[k] + EPSILON) {
      problems.push(`branche ${k} : ${total} dépasse le plafond ${META_POWER_CAP[k]}`);
    }
  }

  // Le scénario du joueur qui a TOUT acheté — le seul qui teste vraiment le
  // plafond composite.
  const all = new Set(nodes.map((n) => n.id));
  const composite = compositePower(metaBonuses(nodes, all));
  if (composite > META_COMPOSITE_CAP + EPSILON) {
    problems.push(`composite maximal ${composite} dépasse le plafond ${META_COMPOSITE_CAP}`);
  }

  // Un arbre plus cher que la totalité des points existants contient des nœuds
  // que PERSONNE ne peut acheter — du contenu mort affiché comme accessible.
  const budget = skillPointsEarned(milestones, milestoneIds);
  const cost = totalSkillCost(nodes);
  if (cost > budget) {
    problems.push(`arbre STAT à ${cost} points pour ${budget} points disponibles à vie`);
  }

  return problems;
}
